import {
  type AgenticLogicPlacementAssignmentDto,
  type AgenticLogicPlacementDto,
  type AgenticLogicRuleDeploySummaryDto,
  type AgenticLogicRuleDto,
  type AgenticLogicRuleState,
  type AgenticLogicSurface,
  type AgenticLogicTemplateSummaryDto,
  type ExperienceCategory,
} from "@intelligems/ig-types";

import {
  describeAttachment,
  MISSING_REFERENCE_LABEL,
} from "../assignments/assignmentRows";
import { visibleSurfaces } from "./surfaceLabels";

/**
 * One place this rule serves, as the merchant names it: the experience and
 * variation when the assignment is variation-scoped, otherwise the placement
 * it backstops store-wide. Row identity is the ASSIGNMENT, not the placement,
 * because one placement can serve several experiences and each of them is a
 * separate answer to "where does my change land".
 */
export interface RuleListUsage {
  assignmentId: string;
  name: string;
  /** Qualifies a usage that is not one experience; null when the name is one. */
  detail: string | null;
  /** The experience this usage opens, when the assignment resolves one. */
  experience: { id: string; category: ExperienceCategory } | null;
}

export interface RuleListRow {
  rule: AgenticLogicRuleDto;
  /**
   * The rule's deploy outcome, or `null` when nothing has been deployed for it
   * yet. Carried on the row rather than derived here so the ONE derivation in
   * `ruleDisplayStatus` — which also needs the org's Advanced verdict, a thing
   * this pure builder has no business reading — stays the only place a rule's
   * state becomes something a reader sees.
   */
  deploy: AgenticLogicRuleDeploySummaryDto | null;
  /** Null means eligibility could not be supplied for this rule. */
  eligibleSurfaces: AgenticLogicSurface[] | null;
  /** Null means assignments or placements could not be loaded. */
  usedIn: RuleListUsage[] | null;
}

interface BuildRuleListRowsInput {
  rules: readonly AgenticLogicRuleDto[];
  deploys?: readonly AgenticLogicRuleDeploySummaryDto[];
  templates?: readonly AgenticLogicTemplateSummaryDto[];
  placements?: readonly AgenticLogicPlacementDto[];
  assignments?: readonly AgenticLogicPlacementAssignmentDto[];
}

export const buildRuleListRows = ({
  rules,
  deploys,
  templates,
  placements,
  assignments,
}: BuildRuleListRowsInput): RuleListRow[] => {
  const deployByRuleId = new Map(
    (deploys ?? []).map((deploy) => [deploy.ruleId, deploy])
  );
  const templatesById = new Map(
    (templates ?? []).map((template) => [template.id, template])
  );
  const placementsById = new Map(
    (placements ?? []).map((placement) => [placement.id, placement])
  );
  const usageByRuleId =
    placements === undefined || assignments === undefined
      ? null
      : buildUsageByRuleId(assignments, placementsById);

  // The two rule kinds answer this from different places, and neither is a
  // constant any more: a code rule declares its surfaces when it is created, a
  // builder rule inherits its template's. A code rule with no declaration is
  // one created before they existed and never backfilled; it reads as unknown
  // rather than as "everywhere".
  const eligibleSurfacesOf = (
    rule: AgenticLogicRuleDto
  ): AgenticLogicSurface[] | null => {
    if (rule.managedBy === "code") {
      if (!rule.targetSurfaces) {
        return null;
      }
      return visibleSurfaces(rule.targetSurfaces);
    }
    if (templates === undefined || !rule.templateId) {
      return null;
    }
    const template = templatesById.get(rule.templateId);
    if (!template) {
      return null;
    }
    return visibleSurfaces(template.eligibleSurfaces);
  };

  return rules
    .map((rule) => ({
      rule,
      deploy: deployByRuleId.get(rule.id) ?? null,
      eligibleSurfaces: eligibleSurfacesOf(rule),
      usedIn: usageByRuleId?.get(rule.id) ?? (usageByRuleId ? [] : null),
    }))
    .sort(
      (left, right) =>
        ruleListRank(left) - ruleListRank(right) ||
        right.rule.lastUpdateTs - left.rule.lastUpdateTs
    );
};

const STATE_RANK: Record<AgenticLogicRuleState, number> = {
  active: 0,
  paused: 2,
  draft: 3,
};

/**
 * Serving rules come first, and an active rule that is actually attached to a
 * placement outranks one that is not; everything else falls back to its state.
 * Unavailable usage (`null`) cannot claim the attached tier.
 */
const ruleListRank = ({ rule, usedIn }: RuleListRow): number =>
  rule.state === "active" ? (usedIn?.length ? 0 : 1) : STATE_RANK[rule.state];

const buildUsageByRuleId = (
  assignments: readonly AgenticLogicPlacementAssignmentDto[],
  placementsById: ReadonlyMap<string, AgenticLogicPlacementDto>
): Map<string, RuleListUsage[]> => {
  const usages = new Map<string, RuleListUsage[]>();

  for (const assignment of assignments) {
    const forRule = usages.get(assignment.ruleId) ?? [];
    forRule.push(
      describeUsage(
        assignment,
        placementsById.get(assignment.placementId)?.name ??
          MISSING_REFERENCE_LABEL
      )
    );
    usages.set(assignment.ruleId, forRule);
  }

  return new Map(
    [...usages.entries()].map(([ruleId, forRule]) => [
      ruleId,
      [...forRule].sort((left, right) => left.name.localeCompare(right.name)),
    ])
  );
};

/**
 * Names a usage the way the assignments grid names the same row, so the two
 * screens never disagree about what a rule is attached to. A global row is the
 * placement's store-wide default rather than one experience, so it keeps the
 * placement name and says so instead of pretending to be an experience.
 */
const describeUsage = (
  assignment: AgenticLogicPlacementAssignmentDto,
  placementName: string
): RuleListUsage => {
  const attachment = describeAttachment(assignment);
  switch (attachment.kind) {
    case "experience":
      return {
        assignmentId: assignment.id,
        name: `${attachment.experienceName} — ${attachment.variationName}`,
        detail: null,
        experience: assignment.experience
          ? {
              id: assignment.experience.id,
              category: assignment.experience.category,
            }
          : null,
      };
    case "unresolved-variation":
      return {
        assignmentId: assignment.id,
        name: placementName,
        detail: "experiment variation no longer exists",
        experience: null,
      };
    // A row reached through an assignment is attached by construction, so
    // "none" cannot occur here and falls in with the store-wide default.
    case "global":
    case "none":
      return {
        assignmentId: assignment.id,
        name: placementName,
        detail: "all experiences",
        experience: null,
      };
  }
};
